import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import * as React from 'react';

import { definitionsApi } from '../api/definitions-api';
import { internalCatalogueApi } from '../api/internal-catalogue-api';
import type {
  IInternalFieldDetail,
  IWriteInternalFieldGroup
} from '../api/internal-catalogue-contracts';
import type { IInternalFormSchema } from '../api/internal-field-contracts';
import { internalFieldsApi } from '../api/internal-fields-api';
import { AdminPage, LoadError, STAFF_FIELD_TABS, useAdminData } from '../components/admin-page';
import { refusalMessage } from '../refusal';

/**
 * One survey's staff form (SR-226) — its sections, their order, and where each field sits. What
 * this screen writes is exactly what the record page draws under *Veritant research*.
 *
 * **Two reads, because the two facts live apart.** The catalogue
 * (`GET /admin/internal-fields?surveyCode=`) says which fields the survey has and their
 * bindings; it carries a section's **code** and never its name. The names and the sections' own
 * order are §11.1's schema document, which is the same read the record page draws from — so
 * what is shown here is what a reader will see. The fields are taken from the catalogue rather
 * than from the schema because the schema omits a retired field, and a retired field still has
 * a placement to repair.
 *
 * **The schema is held per page load and the sections are not re-read from it.** That read is
 * shared with every hub on the page (`internal-fields-api.ts`), so it cannot be invalidated
 * from here; instead the sections are state, seeded from it once and replaced by the answer to
 * every `PUT …/groups`, which returns them as stored. A reload reads the schema afresh.
 *
 * **Three routes, three gestures.** Renaming or reordering sections is one whole-block write
 * (`groups`); moving a field to another section is its binding (`bind`); moving it up or down is
 * the survey's one placement route (`order`), which takes **every** bound field in the order it
 * is to be drawn — so the list sent is rebuilt from what is on screen, never patched.
 */
export function AdminStaffFieldFormsPage() {
  const [surveyCode, setSurveyCode] = React.useState<Nullable<string>>(null);

  const { data: surveys, error: surveysError } = useAdminData(
    (signal) => definitionsApi.surveys(signal),
    []
  );

  // The first survey, once they land: a form screen with nothing chosen says nothing at all.
  React.useEffect(() => {
    if (surveyCode === null && surveys !== null && surveys.length > 0) {
      setSurveyCode(surveys[0].code);
    }
  }, [surveyCode, surveys]);

  return (
    <AdminPage title="Staff fields" tabs={STAFF_FIELD_TABS} documentTitle="Forms">
      {surveysError && <LoadError message={surveysError} />}

      <div className="flex flex-wrap items-center gap-3">
        <Select value={surveyCode ?? ''} onValueChange={setSurveyCode}>
          <SelectTrigger className="w-72" aria-label="Survey">
            <SelectValue placeholder="Choose a survey" />
          </SelectTrigger>
          <SelectContent>
            {(surveys ?? []).map((survey) => (
              <SelectItem key={survey.code} value={survey.code}>
                {survey.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {surveyCode !== null && <SurveyForm key={surveyCode} surveyCode={surveyCode} />}
    </AdminPage>
  );
}

function SurveyForm({ surveyCode }: { surveyCode: string }) {
  const { data, error, setError, reload } = useAdminData(
    async (signal) => {
      const fields = await internalCatalogueApi.list({ surveyCode, includeRetired: true }, signal);
      // A survey whose form cannot be read still has a catalogue to repair: its sections then
      // stand by their codes, which is what the bindings carry.
      const schema = await internalFieldsApi.schema(surveyCode).catch(() => null);
      return { fields, schema };
    },
    [surveyCode]
  );

  if (error !== null && data === null) return <LoadError message={error} />;
  if (data === null) return <Skeleton className="h-64 w-full" />;

  // Derived, not held: every write ends in a re-read, so state here would be overwritten by the
  // next `data` anyway — and `Sections` keeps the author's draft, keyed by the survey.
  const placed = seedSections(data.fields, data.schema);
  const drawn = groupsInOrder(data.fields, placed, surveyCode);
  const order = drawn.flatMap((group) => group.fields.map((field) => field.code));

  const write = async (run: () => Promise<unknown>, fallback: string) => {
    try {
      await run();
      void reload();
    } catch (failure) {
      setError(refusalMessage(failure, fallback));
    }
  };

  return (
    <div className="grid gap-6">
      {/* The same words a refusal gets on the Fields tab: one feature must not say a refused
          write two ways, and the suite's one accessor has to find both. */}
      {error !== null && <LoadError message={error} />}

      {data.schema === null && (
        <Alert>
          <AlertTitle>The form itself could not be read</AlertTitle>
          <AlertDescription>
            The sections stand by their codes. Everything below is still the catalogue’s and
            still writable.
          </AlertDescription>
        </Alert>
      )}

      <Sections
        key={surveyCode}
        sections={placed}
        // With no schema the names are codes; saving would write them over the real ones.
        readOnly={data.schema === null}
        onSave={(next) =>
          void write(
            () => internalCatalogueApi.setGroups(surveyCode, next),
            'Could not save the sections'
          )
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>The form, in order</CardTitle>
          <CardDescription>
            This is the order the record page draws. Moving a field rewrites the whole survey’s
            placement, which is the only way one is saved.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5">
          {drawn.map((group) => (
            <section key={group.code ?? ''} className="grid gap-2">
              <h3 className="text-sm font-semibold tracking-wide uppercase">
                {group.name ?? 'On no section of the form'}
              </h3>
              {group.fields.length === 0 && (
                <p className="text-sm text-muted-foreground">No field sits here.</p>
              )}
              {group.fields.map((field) => (
                <div
                  key={field.code}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2 text-sm"
                >
                  <span>{field.name}</span>
                  <code className="text-xs text-muted-foreground">{field.code}</code>
                  <span className="text-xs text-muted-foreground">{field.type}</span>
                  {field.retired && (
                    // survey-api refuses every binding write on a retired field (409), so its
                    // row is drawn and its controls are not offered.
                    <span className="text-xs text-muted-foreground">
                      retired — bring it back under Fields to place it
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-2">
                    <Select
                      value={group.code ?? UNSECTIONED}
                      disabled={field.retired}
                      onValueChange={(value) =>
                        void write(
                          () =>
                            // A rebind writes the whole binding: survey-api assigns the body's
                            // `labelOverride` and `condition` unconditionally, so what is stored
                            // is sent back with the new group or it is nulled.
                            internalCatalogueApi.bind(field.code, surveyCode, {
                              group: value === UNSECTIONED ? null : value,
                              labelOverride: field.labelOverride,
                              condition: field.condition
                            }),
                          'Could not move the field'
                        )
                      }
                    >
                      <SelectTrigger
                        className="w-48"
                        aria-label={`Section for ${field.name}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNSECTIONED}>On no section</SelectItem>
                        {placed.map((section) => (
                          <SelectItem key={section.code} value={section.code}>
                            {section.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label={`Move ${field.name} up`}
                      // Within the section: the page re-sorts by section first, so a swap
                      // across a boundary writes and draws nothing. Between sections is the
                      // picker beside it.
                      disabled={field.retired || group.fields[0].code === field.code}
                      onClick={() =>
                        void write(
                          () =>
                            internalCatalogueApi.reorder(
                              surveyCode,
                              swapped(order, order.indexOf(field.code), -1)
                            ),
                          'Could not move the field'
                        )
                      }
                    >
                      <ChevronUp />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label={`Move ${field.name} down`}
                      disabled={
                        field.retired ||
                        group.fields[group.fields.length - 1].code === field.code
                      }
                      onClick={() =>
                        void write(
                          () =>
                            internalCatalogueApi.reorder(
                              surveyCode,
                              swapped(order, order.indexOf(field.code), 1)
                            ),
                          'Could not move the field'
                        )
                      }
                    >
                      <ChevronDown />
                    </Button>
                  </div>
                </div>
              ))}
            </section>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

/** The section picker's "none": a `Select` cannot hold an empty string, and a field on no
 *  section is where survey-api's trailing group comes from. */
const UNSECTIONED = 'none';

function Sections({
  sections,
  readOnly,
  onSave
}: {
  sections: IWriteInternalFieldGroup[];
  readOnly: boolean;
  onSave: (sections: IWriteInternalFieldGroup[]) => void;
}) {
  const [rows, setRows] = React.useState(sections);
  React.useEffect(() => setRows(sections), [sections]);
  // A section's code is its key: survey-api matches a binding to it, so an existing one is
  // drawn and a new one is typed.
  const stored = new Set(sections.map((section) => section.code));

  const move = (index: number, by: number) => setRows(swapped(rows, index, by));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sections</CardTitle>
        <CardDescription>
          Saved whole, and the order here is the order they are drawn in. A section a field still
          sits in cannot be removed — move the field first.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        {rows.map((section, index) => (
          <div key={index} className="flex flex-wrap items-center gap-2">
            {stored.has(section.code) ? (
              <code className="w-48 text-xs text-muted-foreground">{section.code}</code>
            ) : (
              <Input
                value={section.code}
                onChange={(event) =>
                  setRows(
                    rows.map((row, at) =>
                      at === index ? { ...row, code: event.target.value } : row
                    )
                  )
                }
                placeholder="section_code"
                className="w-48 font-mono"
                aria-label={`Code of section ${index + 1}`}
              />
            )}
            <Input
              value={section.name}
              onChange={(event) =>
                setRows(
                  rows.map((row, at) =>
                    at === index ? { ...row, name: event.target.value } : row
                  )
                )
              }
              className="max-w-sm flex-1"
              aria-label={`Name of section ${index + 1}`}
              disabled={readOnly}
            />
            <Button
              variant="outline"
              size="icon"
              aria-label={`Move section ${section.code} up`}
              disabled={index === 0}
              onClick={() => move(index, -1)}
            >
              <ChevronUp />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label={`Move section ${section.code} down`}
              disabled={index === rows.length - 1}
              onClick={() => move(index, 1)}
            >
              <ChevronDown />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRows(rows.filter((_, at) => at !== index))}
            >
              Remove
            </Button>
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={readOnly}
            onClick={() => setRows([...rows, { code: '', name: '' }])}
          >
            <Plus />
            Add a section
          </Button>
          <Button className="ml-auto" disabled={readOnly} onClick={() => onSave(rows)}>
            Save sections
          </Button>
        </div>
        {readOnly && (
          <p className="text-xs text-muted-foreground">
            The form could not be read, so these are the codes the bindings carry and not the
            names a reader sees — saving them would write a code over every name.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** A list with the item at `index` a step up or down — a section in its block, a field in the
 *  survey's placement. Out of bounds returns the list, so a disabled button that is pressed
 *  anyway writes what is already stored. */
function swapped<T>(list: T[], index: number, by: number): T[] {
  const to = index + by;
  if (index < 0 || to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

/**
 * The survey's sections, to edit and to save back whole.
 *
 * **The schema's are not enough.** §11.1 omits a group holding no field the caller may see —
 * one with only retired fields in it, or none at all — and `PUT …/groups` replaces the whole
 * block: saving the schema's list alone would delete those silently, or be refused naming a
 * field this screen never drew. So the list is the schema's, in its order, **plus every group
 * code the bindings carry that the schema left out**, appended by its code.
 *
 * With no schema at all the codes are all there is, and each stands by its own — which is why
 * the editor is read-only in that arm: saving would rename every section to its code.
 */
function seedSections(
  fields: IInternalFieldDetail[],
  schema: Nullable<IInternalFormSchema>
): IWriteInternalFieldGroup[] {
  const named = (schema?.groups ?? [])
    .filter((group) => group.code !== null && group.name !== null)
    .map((group) => ({ code: group.code as string, name: group.name as string }));
  const known = new Set(named.map((group) => group.code));
  for (const field of fields) {
    for (const binding of field.bindings) {
      if (binding.groupCode !== null && !known.has(binding.groupCode)) {
        known.add(binding.groupCode);
        named.push({ code: binding.groupCode, name: binding.groupCode });
      }
    }
  }
  return named;
}

/** The drawn order cut into its sections, the trailing one last — what the page renders. A
 *  section with nothing in it is kept, because an empty section is a thing to move a field into
 *  and the only place it can be seen. */
function groupsInOrder(
  fields: IInternalFieldDetail[],
  sections: IWriteInternalFieldGroup[],
  surveyCode: string
): IDrawnSection[] {
  const drawn: IDrawnSection[] = sections.map((section) => ({
    code: section.code,
    name: section.name,
    fields: []
  }));
  const trailing: IDrawnSection = { code: null, name: null, fields: [] };
  const into = new Map(drawn.map((section) => [section.code, section]));

  for (const field of fields) {
    const binding = field.bindings.find((candidate) => candidate.surveyCode === surveyCode);
    if (binding === undefined) continue;
    const section =
      binding.groupCode === null ? trailing : (into.get(binding.groupCode) ?? trailing);
    section.fields.push({
      code: field.code,
      name: field.name,
      type: field.type,
      retired: field.retired,
      labelOverride: binding.labelOverride,
      condition: binding.condition,
      ordinal: binding.ordinal
    });
  }
  // Within a section, the binding's own ordinal — the schema's rule, applied to the catalogue's
  // rows. The sections are already in the form's order.
  for (const section of [...drawn, trailing]) {
    section.fields.sort(
      (left, right) => left.ordinal - right.ordinal || left.code.localeCompare(right.code)
    );
  }
  return trailing.fields.length > 0 ? [...drawn, trailing] : drawn;
}

interface IDrawnSection {
  code: Nullable<string>;
  name: Nullable<string>;
  fields: IDrawnField[];
}

/** One row of the form: what it is, and the binding members a rebind has to send back. */
interface IDrawnField {
  code: string;
  name: string;
  type: string;
  retired: boolean;
  labelOverride: Nullable<string>;
  condition: Nullable<unknown>;
  /** The binding's own, which orders the section. */
  ordinal: number;
}
